import React, { useEffect, useRef, useState } from 'react';

/**
 * Custom state hook: An easy way to handle making an API call and
 * managing the loading and error states without extra fetch-related code
 * @param input URL to call
 * @param fetchFn Optional, Must pass a custom fetch function for authentication, ommit this to use default javascript fetch.
 * @param init Optional, other parameters: e.g. headers, body, etc
 * @param dontCallImmediately Whether to stop the initial fetch happening on load, until refetch called
 *
 * Values to be return into an ordered array:
 *  [
 * <response data>,
 * <loading boolean>,
 * <error ErrorGeneric>,
 * <performFetch function>
 * ]
 *
 * Examples:
 * [data, isLoading, dataError, feFetch] = useFetch("www.google.com/data"),
 * [data, isLoading, dataError, feFetch] = useFetch("www.google.com/data", fetch),
 * [personalData, isLoading, dataError, feFetch] = useFetch("www.google.com/myprofile", customAuthFetchFunction)
 */
export const useFetch = <R,>(
  input: RequestInfo,
  fetchFn?: (
    input: RequestInfo,
    init?: RequestInit | undefined
  ) => Promise<Response>,
  init?: RequestInit | undefined,
  dontCallImmediately?: boolean,
  transform?: (response: R) => any
) => {
  const abortControllerRef = useRef<AbortController>(new AbortController());
  let errorCode: number | undefined = undefined;
  let errorText: string | undefined = undefined;
  let errorJson: any | undefined = undefined;

  const [error, setError] = useState<ErrorGeneric | undefined>(undefined);

  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState(null);

  const performFetch = async (overrideInit?: RequestInit | undefined) => {
    // Cancel previous request ongoing if not yet complete.
    if (loading) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
    // Create signal for abortion in case of unmount or refetch
    const { signal } = abortControllerRef.current;
    errorCode = undefined;
    errorText = undefined;
    errorJson = undefined;
    setError(undefined);
    setLoading(true);

    try {
      if (!input) {
        throw new Error(`URL is a required parameter of a fetch`);
      }

      // Override fetch function with custom fetch function, if provided
      const fetchFunction = fetchFn ? fetchFn : fetch;

      // Override init, if one provided (Ideal for POST/PUT calls that arent called immediately)
      const finalInit = overrideInit ? overrideInit : init;

      // await fetch with given fetch function, including signal and input + init
      const fetchResponse: Response = await fetchFunction(input, {
        ...finalInit,
        signal
      });
      let data;

      // Handle HTTP errors or return json
      try {
        if (fetchResponse.ok) {
          const MIMEtype = fetchResponse.headers.get('content-type');
          // Check mime type, and format data appropriately
          if (MIMEtype) {
            if (
              MIMEtype.includes('application/json') ||
              MIMEtype.includes('application/ld+json')
            ) {
              data = await fetchResponse.json();
            } else if (MIMEtype.includes('image')) {
              data = await fetchResponse.blob();
            } else {
              data = await fetchResponse.text();
            }
          }
        } else {
          console.error(
            `Error ${fetchResponse.status} in fetch: `,
            fetchResponse.statusText
          );

          // Clone required for multiple uses of response, otherwise locked
          const responseClone = fetchResponse.clone();
          let jsonError: unknown;
          let textError = 'Unknown Error';

          /**
           * Tries to get JSON error. If JSON error turns out to be a string, puts it in the errorString not jsonString.
           *
           */
          try {
            const jsonConversion = await responseClone.json();
            if (typeof jsonConversion === 'string') {
              textError = jsonConversion;
            } else {
              jsonError = jsonConversion;
            }
          } catch {
            /**
             * If cant be json parsed, try as text
             */
            try {
              textError = await responseClone.text();
            } catch {
              /**
               * If it can't find any way to process the error, give one of the predefined user friendly errors
               */
              textError = userFriendlyErrorString(responseClone);
            }
          }

          errorCode = fetchResponse.status;
          // Some errors come though in string form with quotation marks around them for some reason, this removes them
          errorText = removeSurroundingQuotesFromBadlyWrittenErrors(textError);
          errorJson = jsonError;
        }
      } catch (error) {
        console.error(
          `Error in useFetch: Response not in JSON format: ${error}`
        );
      } finally {
        if (data && transform) {
          setResponse(transform(data));
        } else {
          setResponse(data);
        }
      }
    } catch (error: any) {
      /**
       * Do not perform any setState if fetch was aborted,
       * This is important, as you shouldnt perform state changes on unmounted components
       */
      if (error.name !== 'AbortError') {
        console.error(`Error in useFetch: ${error.message}`);
        errorText = 'Something went wrong. Request could not be completed.';
      }
    }
    if (errorCode || errorText || errorJson) {
      setError({
        code: errorCode,
        errorText: errorText,
        errorJson: errorJson
      });
    }
    setLoading(false);
  };

  const removeSurroundingQuotesFromBadlyWrittenErrors = (
    errorString: string
  ) => {
    let splitStr = errorString.split('');
    if (splitStr[0] === '"' && splitStr[splitStr.length - 1] === '"') {
      splitStr.pop();
      splitStr.shift();
    }
    return splitStr.join('');
  };

  const userFriendlyErrorString = (failureResponse: Response): string => {
    let errorStatusText;

    switch (failureResponse.status) {
      case 400:
        errorStatusText = 'There was a problem with the request.';
        break;
      case 401:
        errorStatusText = 'You must be logged in to perform this action.';
        break;
      case 403:
        errorStatusText =
          'You are do not have permission to perform this action.';
        break;
      case 404:
        errorStatusText = 'There was a problem getting to the server.';
        break;
      case 408:
        errorStatusText = 'Request took too long to complete.';
        break;
      case 409:
        errorStatusText = 'There was a conflict when performing this action.';
        break;
      case 415:
        errorStatusText = 'This file type is not supported.';
        break;
      case 500:
        errorStatusText = 'There was a problem with the server.';
        break;
      case 501:
        errorStatusText = 'This feature has not been implemented yet.';
        break;
      case 502:
        errorStatusText =
          'There was a problem with the gateway. Please contact support.';
        break;
      case 503:
        errorStatusText = 'This service is currently unavailable.';
        break;
      case 504:
        errorStatusText = 'The servers may be down. Please try again later.';
        break;
      default:
        errorStatusText = 'Unknown Error';
    }

    return errorStatusText;
  };

  // Initially run
  useEffect(() => {
    if (!dontCallImmediately) {
      performFetch();
    }

    // Cleanup: Abort ongoing requests on component unmount
    return () => {
      abortControllerRef.current.abort();
    };
  }, []);

  const useStateResponse: IUseFetch<any> = [
    response,
    loading,
    error,
    performFetch
  ];

  return useStateResponse;
};

/**
 * Just add your interface to this generic as such:
 * IUseFetch<IMyInterface>, in order for the first (data) field
 * to have that type
 */
export type IUseFetch<T> = [
  T,
  boolean,
  ErrorGeneric | undefined,
  (overrideInit?: RequestInit | undefined) => Promise<void>
];

export default useFetch;

// // For testing purposes
// // ----------------------------------------------------
// export const TestAPIErrorsComponent = () => {
//   // Successful
//   const ok200 = 'http://www.mocky.io/v2/5e386a0d3100005a00d381df'; // Body - text/plain: Hello world!

//   // Redirects / "Unknown Error"
//   const tempRedirect307 = 'http://www.mocky.io/v2/5e386aab310000e489d381e8'; // With no redirect url set

//   // Client Errors
//   const badReq400 = 'http://www.mocky.io/v2/5e38613d310000719bd381b3';
//   const unAuth401 = 'http://www.mocky.io/v2/5e386222310000e489d381b7';
//   const forbidden403 = 'http://www.mocky.io/v2/5e3862783100008d87d381ba';
//   const notFound404 = 'http://www.mocky.io/v2/5e38629e3100000f00d381bc';
//   const conflict409 = 'http://www.mocky.io/v2/5e3862bd3100005a00d381bf';

//   // Server Errors
//   const internalSvrErr500 = 'http://www.mocky.io/v2/5e3862f3310000b2c1d381c0';
//   const notImplemented501 = 'http://www.mocky.io/v2/5e38638a310000719bd381c1';
//   const badGateway502 = 'http://www.mocky.io/v2/5e3863d1310000e389d381c2';
//   const svcUnavailable503 = 'http://www.mocky.io/v2/5e3863ec310000e489d381c3';
//   const gatewayTimeout =
//     'http://www.mocky.io/v2/5e38641f310000e489d381c5?mocky-delay=1000ms';

//   const [data, isLoading, error] = useFetch(notFound404);

//   return (
//     <>
//       {data ? data : null}
//       {isLoading ? 'Loading...' : null}
//       {error ? error : null}
//     </>
//   );
// };

export interface ErrorGeneric<T = any> {
  code: number | undefined;
  errorText: string | undefined;
  errorJson: T | undefined;
}
